import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TilbyDatePipe } from '@tilby/tilby-ui-lib/pipes/tilby-date';
import { $state, restManager, util } from 'app/ajs-upgraded-providers';
import * as Papa from 'papaparse';
import { SystemUtilsService } from 'src/app/core';
import { AlertDialogService, ItemLabelsDialogService } from 'src/app/dialogs';
import { blobToDataURL } from 'src/app/shared/data-conversion-utils';
import { StockMovementsFilterService } from './stock-movements-filter.service';

// colonne del CSV
const EXPORT_COLUMNS = ['code', 'barcode', 'name', 'quantity', 'options_values', 'combination', 'supplier_name', 'supplier_order', 'type', 'cause', 'notes', 'date'];
const EXPORT_PICKED_FIELDS = ['name', 'barcode', 'code', 'options_values', 'combination', 'supplier_name', 'supplier_order', 'quantity', 'notes'];

@Injectable()
export class StockMovementsActionsService {

  private readonly restManagerService = inject(restManager);
  private readonly utilService = inject(util);
  private readonly state = inject($state);
  private readonly translateService = inject(TranslateService);
  private readonly tilbyDatePipe = inject(TilbyDatePipe);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly itemLabelsDialogService = inject(ItemLabelsDialogService);
  private readonly filterService = inject(StockMovementsFilterService);
  private readonly systemUtilsService = inject(SystemUtilsService);

  private getBatchMovements(batch: any): Promise<any[]> {
    return this.restManagerService.getList('stock_movements', {
      date: batch.date,
      type: batch.type,
      stock_location_id: batch.stock_location_id,
      pagination: false
    });
  }

  // filtri correnti (con la ricerca testuale, se attiva) senza paginazione, per l'export completo
  private getExportQuery(): Record<string, any> {
    const filters = this.filterService.searchText()
      ? this.filterService.buildSearchQuery()
      : this.filterService.buildQuery();

    return { ...filters, orderby_desc: 'date', pagination: false };
  }

  // scarica il CSV di tutti i movimenti che corrispondono ai filtri correnti
  async downloadMovements() {
    const result = await this.restManagerService.downloadOne('stock_movements', 'export', this.getExportQuery());
    this.utilService.downloadFile(result.data, 'export_csv_movements.csv', 'text/plain;charset=utf-8;');
  }

  // condivide lo stesso CSV tramite il menu di condivisione del sistema (app mobile)
  async shareMovements() {
    const result = await this.restManagerService.downloadOne('stock_movements', 'export', this.getExportQuery());
    const title = 'export_csv_movements';
    const dataUrl = await blobToDataURL(result.data);

    return this.systemUtilsService.share({
      title,
      text: title,
      files: [`df:${title}.csv;${dataUrl}`],
    });
  }

  async exportBatch(batch: any) {
    const batchMovements = await this.getBatchMovements(batch);

    const movementsForCSV = batchMovements.map(movement => {
      const row: Record<string, any> = {};
      EXPORT_PICKED_FIELDS.forEach(field => {
        if (movement[field] !== undefined) row[field] = movement[field];
      });

      if (movement.type === 'load') {
        row['cause'] = this.translateService.instant('STOCK.CAUSES.' + String(movement.load_cause).toUpperCase());
      } else if (movement.type === 'unload') {
        row['cause'] = this.translateService.instant('STOCK.CAUSES.' + String(movement.unload_cause).toUpperCase());
      }

      row['date'] = this.tilbyDatePipe.transform(movement.date);
      row['type'] = this.translateService.instant('STOCK.TOPBAR_STOCK.' + String(movement.type).toUpperCase());

      return row;
    });

    const csv = Papa.unparse(movementsForCSV, {
      columns: EXPORT_COLUMNS,
      delimiter: ';',
      header: true,
    });

    this.utilService.downloadFile(csv, 'export_csv_movements.csv', 'text/plain;charset=utf-8;');
  }

  async generateLabels(batch: any) {
    const results = await this.getBatchMovements(batch);
    const itemMovements = results.filter(movement => movement.entry_type === 'item');

    const itemsMap = await this.utilService.getItemsFromIds(itemMovements);
    const foundItems: any[] = Object.values(itemsMap || {}).filter(Boolean);

    if (!foundItems.length) {
      return this.alertDialogService.openDialog({
        data: { messageLabel: 'STOCK.MOVEMENTS.LABELS_GENERATOR_NO_ITEMS' }
      });
    }

    // quantità totale per prodotto, usata come numero di etichette
    const quantities = new Map<number, number>();
    itemMovements.forEach(movement => {
      quantities.set(movement.item_id, (quantities.get(movement.item_id) ?? 0) + movement.quantity);
    });

    foundItems.forEach(item => {
      if (item?.id && quantities.get(item.id)) {
        item._quantity = quantities.get(item.id);
      }
    });

    return this.itemLabelsDialogService.openDialog({
      data: {
        promotion: { base_pricelist: 1 } as any,
        items: structuredClone(foundItems)
      }
    });
  }

  // crea un nuovo movimento che annulla il batch (carico <-> scarico, un secondo dopo)
  async rollbackBatch(batch: any) {
    const movements = await this.getBatchMovements(batch);

    movements.forEach(movement => {
      movement.date = new Date(new Date(movement.date).getTime() + 1000).toISOString();
      movement.quantity = Math.abs(movement.quantity);

      switch (movement.type) {
        case 'load':
          Object.assign(movement, { type: 'unload', unload_cause: 'negative_adjustment', load_cause: null });
          break;
        case 'unload':
          Object.assign(movement, { type: 'load', load_cause: 'positive_adjustment', unload_cause: null });
          break;
        default:
          break;
      }
    });

    this.state.go('app.new.stock.new-movement', {
      movements,
      stock_location_id: this.filterService.stock_location_id?.value
    });
  }

  async goodsDelivery(batch: any) {
    const movements = await this.getBatchMovements(batch);
    this.state.go('app.new.stock.new-movement', { movements, mode: 'goods_delivery' });
  }
}
